import { asyncRetryIfTransientError } from "../retries";

// MN-9405: envoy/istio emits this 503 body when the sidecar reset/terminated the connection to the
// upstream pod *before any response headers* (typically during pod scaling/drain). No response
// headers reached the client, so the upstream overwhelmingly never produced a result — a single
// retry recovers the request. This mirrors istio/envoy's own `retry_on: connect-failure,reset`
// policy, which retries this class for all methods. Gating on this body — not bare status 503 —
// keeps a genuine app-emitted 503 (which DID reach the app) from being retried. Dominant source of
// the React-cart fault: HB #129838996 (~30k notices, tag add_to_cart).
//
// Residual risk (accepted): "reset before headers" does not *prove* the upstream never processed
// the request — a request applied then cut off in the narrow window before its response headers
// flush would be re-applied. For the non-idempotent add-to-cart (`order.contents.add_in_batch`)
// that means a rare double-add, which the customer can self-correct. We accept this because the
// reset class is non-delivery-dominant, the retry is bounded to one, and the alternative is ~30k
// hard add-to-cart failures. A server-side idempotency key would remove the risk entirely (future).
const CONNECT_RESET_BEFORE_HEADERS = /upstream connect error or disconnect\/reset before headers/i;

// Effective ~600ms before the single retry (asyncRetryWithExponentialBackOff doubles it once):
// short enough to keep add-to-cart snappy, long enough for the sidecar to reroute to a healthy pod.
const CONNECT_RESET_RETRY_DELAY_MS = 300;

const stringBody = response => {
  if (typeof response === "string") return response;
  if (typeof response?.data === "string") return response.data;
  return "";
};

// True for a 503 carrying the envoy connect-reset signature, across both error shapes: fetch
// (error.message / string error.response) and axios (error.response.data).
export const isConnectResetError = error => {
  // Coerce so a string status ("503" from some error shapes) still matches; Number of a missing
  // status is NaN/0, neither of which equals 503.
  if (Number(error?.status ?? error?.response?.status) !== 503) return false;
  return CONNECT_RESET_BEFORE_HEADERS.test(
    `${error?.message ?? ""} ${stringBody(error?.response)}`
  );
};

// Wraps a request thunk with a single connect-reset 503 retry. `skip` (the caller's noRetry) opts out.
export const withConnectResetRetry = (fn, skip) =>
  skip
    ? fn()
    : asyncRetryIfTransientError(fn, {
        errorMatch: isConnectResetError,
        remainingTries: 1,
        retryDelay: CONNECT_RESET_RETRY_DELAY_MS
      });
